import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchBezIshoda, fetchDen, fetchMetkiZapisi, fetchNezakrytoe, fetchOzhidanie, fetchPriyomka, fetchRoznica, fetchZakrytie,
  deleteSostav,
  deleteZapis,
  patchSostav, fetchZapis, postHod, postPravka, postPravkaGostya, postPravkaVremeni, postSostavPerenyat,
  postHochuRanshe, postOtsrochkaBroni, postPravkaKonca, postPredzapis, postPriyomka, postSostav,
  postPodstatus, postPozhelanieMastera, postPrinyataBezBroni,
  postOtkrytChek, postPrinyatOplatu, postPodtverditOplatu, postStornoOplaty, postProceduru,
  type BezIshodaFiltry, type BezIshodaScreen, type DenQuery, type DenScreen, type HodBody, type NezakrytoeScreen,
  type OzhidanieScreen, type PriyomkaScreen, type RoznicaQuery, type RoznicaScreen, type ZakrytieScreen,
  type HochuRansheTelo, type OtsrochkaTelo,
  type PerenosTelo, type PredzapisTelo, type ProdavecRod, type SostavDobavka, type SostavPravka, type SostavRod,
  type ZapisScreen, type CashPaymentBody,
  podtverditCepochku, podtverditProdavcaVKartochke, postavitProdavca, postavitProdavcaTovara, sozdatZapis,
  type NovayaZapis,
  fetchPerepiskaZapisi,
  nachatPerepisku,
  type PerepiskaZapisi,
} from './api';

export { refusalGate, refusalPerenos, refusalPriyomka, refusalText } from '../network/hooks';

/**
 * ЗАПРОСЫ ЭКРАНОВ ЗАПИСИ (`s303`).
 *
 * ⛔ КЛЮЧ КЭША НЕСЁТ ВЕСЬ ЗАПРОС. Оставь в ключе только слово «день» — и
 * переключение даты или филиала показывало бы вчерашние строки под сегодняшней
 * шапкой, пока идёт загрузка. Ровно тот же закон, что у списка клиенток.
 */
export const denKey = (q: DenQuery) => ['zapis', 'den', q] as const;

/**
 * ЗАПРОС ДНЯ ИЗ АДРЕСА — ОДИН СБОРЩИК НА ВСЕ ЭКРАНЫ (`s311`).
 *
 * ⛔ КЛЮЧ КЭША СОБИРАЕТСЯ ИЗ ВСЕХ ЧЕТЫРЁХ ПОЛЕЙ, А НЕ ИЗ ДВУХ. Журнал складывал
 * ключ из даты, филиала, должности и статуса; карточка поверх него — только из
 * даты и филиала. Ключи не совпадали, кэш не находился, и день под открытым
 * окном грузился заново — плитки исчезали на треть секунды и возвращались
 * (жалоба владельца 09.09, повторенная трижды; поймано замером по кадрам:
 * 19 → 0 → 19). Собирает запрос теперь одно место: разойтись им больше негде.
 */
export function denQueryIzAdresa(params: URLSearchParams, zapasnoy?: Partial<DenQuery>): DenQuery {
  /* ⛔ `s1027` G-24: филиал из адреса — только настоящий номер (> 0). `filial_id=0`
     или мусор в ссылке уходили вниз числом и давали серверу 400 вместо дня. */
  const izAdresa = Number(params.get('filial_id'));
  const filial = Number.isInteger(izAdresa) && izAdresa > 0 ? izAdresa : null;

  return {
    date: params.get('date') ?? zapasnoy?.date ?? undefined,
    filial_id: filial !== null ? filial : (zapasnoy?.filial_id ?? undefined),
    dolzhnost: params.get('dolzhnost'),
    status: params.get('status'),
  };
}

export function useDen(query: DenQuery, vklyuchen = true) {
  return useQuery<DenScreen>({
    queryKey: denKey(query),
    queryFn: () => fetchDen(query),
    // `s719`: страница карточки записи не спрашивает журнал дня у того, кому раздел не открыт.
    enabled: vklyuchen,
    /**
     * ⛔ ПРЕЖНИЙ ДЕНЬ ДЕРЖИТСЯ НА ЭКРАНЕ, ПОКА ЕДЕТ НОВЫЙ (жалоба владельца
     * 09.09.2026: «плашки куда-то пропадают»). Без этого любая смена ключа —
     * шаг по дню, переключение филиала, открытие карточки — на секунду
     * очищает сетку: журнал моргает пустотой там, где данные уже были. Второе
     * дно к правке ссылки: даже если ключ разойдётся, пустого экрана не будет.
     */
    placeholderData: (prev) => prev,
    /**
     * ⛔ ОТКРЫТЫЙ ЖУРНАЛ ЖИВЁТ, А НЕ ЗАСТЫВАЕТ (`s316`, `kau1` Y27). День не
     * обновлялся ничем: ни опроса, ни обновления по возвращению в окно
     * (`refetchOnWindowFocus` выключен глобально). Администратор держит
     * расписание открытым всю смену — и не видел ни чужих ходов, ни того, что
     * принесла синхронизация. Заодно оживает линия «сейчас»: её долю считает
     * СЕРВЕР и присылает готовой (`scale.now_pct`), поэтому вместе с ответом
     * приезжает и новое время. Считать её тикером в браузере значило бы завести
     * вторую арифметику той же величины — ровно то, что запрещает договор
     * экранов.
     *
     * ⛔ МИНУТА, А НЕ СЕКУНДЫ. Шаг сетки — четверть часа; чаще минуты линия
     * никуда заметно не сдвинется, а запросов станет в шестьдесят раз больше.
     */
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export const zapisKey = (id: number) => ['zapis', 'karta', id] as const;

export function useZapis(id: number) {
  return useQuery<ZapisScreen>({ queryKey: zapisKey(id), queryFn: () => fetchZapis(id) });
}

/**
 * СОЗДАНИЕ ЗАПИСИ (`s402`, Д-23). После удачи гасим журнал дня и списки: новая
 * запись обязана появиться в сетке сразу, а не после перезагрузки страницы.
 */
export function useSozdatZapis() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (telo: NovayaZapis) => sozdatZapis(telo),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['zapis', 'den'] });
      void qc.invalidateQueries({ queryKey: ['zapis', 'ozhidanie'] });
      void qc.invalidateQueries({ queryKey: ['zapis', 'bez-ishoda'] });
    },
  });
}

export function useZakrytie(id: number, enabled = true) {
  /* `s342`: шаг закрытия живёт в окне карточки — ответ свода берётся, только
     когда шаг открыт, а не при каждом открытии карточки. */
  return useQuery<ZakrytieScreen>({ queryKey: ['zapis', 'zakrytie', id], queryFn: () => fetchZakrytie(id), enabled });
}

/**
 * ДЕНЬГИ-МИНИМУМ КО ДНЮ X (`s521`). Четыре хода, один кэш.
 *
 * ⛔ ОТВЕТ СЕРВЕРА КЛАДЁТСЯ В КЭШ ЦЕЛИКОМ, а не дорисовывается фронтом — тот же
 * закон, что у `useHod`: дорисуй сам, и администратор видел бы не то, что лежит
 * в базе, а то, что фронт про неё думает. Деньги — тем более.
 *
 * ⛔ СОСЕДНИЕ ЭКРАНЫ СБРАСЫВАЮТСЯ: оплата меняет и карточку записи, и «Незакрытое»
 * (там висят долги), и день. Оставь их — и оплаченный визит остался бы должником
 * на соседней вкладке.
 */
function useHodOplaty<T>(id: number, hod: (telo: T) => Promise<ZakrytieScreen>) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: hod,
    onSuccess: (screen) => {
      qc.setQueryData(['zapis', 'zakrytie', id], screen);
      void qc.invalidateQueries({ queryKey: zapisKey(id) });
      void qc.invalidateQueries({ queryKey: ['zapis', 'den'] });
      void qc.invalidateQueries({ queryKey: ['zapis', 'nezakrytoe'] });
    },
  });
}

export function useOtkrytChek(id: number) {
  return useHodOplaty<void>(id, () => postOtkrytChek(id));
}

export function usePrinyatOplatu(id: number) {
  return useHodOplaty<CashPaymentBody>(
    id,
    (telo) => postPrinyatOplatu(id, telo),
  );
}

export function usePodtverditOplatu(id: number) {
  return useHodOplaty<number>(id, (documentId) => postPodtverditOplatu(id, documentId));
}

export function useStornoOplaty(id: number) {
  return useHodOplaty<{ documentId: number; reason_id?: number; prichina?: string }>(
    id,
    ({ documentId, reason_id, prichina }) => postStornoOplaty(id, documentId, { reason_id, prichina }),
  );
}

/** Подтвердить цепочку «кто продал» у позиции (`s318`, `kau1` Y44). */
export function usePodtverditCepochku(id: number) {
  return useHodOplaty<number>(id, (stroka) => podtverditCepochku(id, stroka));
}

/**
 * ⛔ `s336`: ПОСТАВИТЬ ПРОДАВЦА РУКОЙ НА ЭКРАНЕ ЗАКРЫТИЯ. Тот же ход, что и
 * подтверждение, — и возвращает тот же экран, поэтому идёт той же обёрткой.
 */
export function usePostavitProdavcaVZakrytii(id: number) {
  return useHodOplaty<{ stroka: number; rod: ProdavecRod; kto: number | null }>(
    id,
    ({ stroka, rod, kto }) => postavitProdavca(id, stroka, { rod, id: kto, ekran: 'zakrytie' }),
  );
}

/** `s966` D5: продавец товара на экране закрытия — та же обёртка, тот же экран в ответ. */
export function usePostavitProdavcaTovaraVZakrytii(id: number) {
  return useHodOplaty<{ stroka: number; rod: 'staff' | 'user'; kto: number }>(
    id,
    ({ stroka, rod, kto }) => postavitProdavcaTovara(id, stroka, rod, kto),
  );
}

/**
 * ⛔ `s336`: ПОДТВЕРЖДЕНИЕ И СМЕНА ПРОДАВЦА ПРЯМО В КАРТОЧКЕ — там, где состав
 * и правится. Слово владельца: «показывает его тому, кто вносит изменение,
 * прямо в момент внесения». Обе двери возвращают ЭКРАН КАРТОЧКИ.
 */
export function usePodtverditProdavca(id: number) {
  return usePravkaEkrana(id, (stroka: number) => podtverditProdavcaVKartochke(id, stroka));
}

export function usePostavitProdavca(id: number) {
  return usePravkaEkrana(
    id,
    ({ stroka, rod, kto }: { stroka: number; rod: ProdavecRod; kto: number | null }) =>
      postavitProdavca(id, stroka, { rod, id: kto, ekran: 'zapis' }),
  );
}

export function useOzhidanie(filialId: number | null) {
  return useQuery<OzhidanieScreen>({ queryKey: ['zapis', 'ozhidanie', filialId], queryFn: () => fetchOzhidanie(filialId) });
}

export function useRoznica(q: RoznicaQuery) {
  return useQuery<RoznicaScreen>({
    queryKey: ['zapis', 'roznica', q],
    queryFn: () => fetchRoznica(q),
    /**
     * ⛔⛔ ПРЕЖНЯЯ ВЫДАЧА ДЕРЖИТСЯ НА ЭКРАНЕ, ПОКА ЕДЕТ НОВАЯ (`s341`, Н12).
     *
     * Правило заведено у журнала (`useDen`) по жалобе владельца 09.09.2026
     * «плашки куда-то пропадают» — и НЕ доехало до соседних экранов раздела.
     * Цена: фильтры и даты входят в ключ кэша, а новый ключ у `useQuery` —
     * это `isLoading`, а не `isFetching`. Страница по `isLoading` подменяет
     * ВЕСЬ экран заглушкой загрузки, панель фильтров разбирается вместе с ним
     * и приезжает обратно ЗАКРЫТОЙ.
     *
     * Так и читается находка Н12 («при попытке выбрать дату „с" — сразу
     * открывает этот день»): владелец выбирал «С даты», панель схлопывалась,
     * на экране оставалась узкая выдача — и второе поле, «По дату», выбрать
     * было уже нечем.
     */
    placeholderData: (prev) => prev,
  });
}

/**
 * ⛔ РАЗМЕР ОКНА ВХОДИТ В КЛЮЧ КЭША (`s305`, §3.3). Забудь его — и «Показать
 * ещё» отдало бы прежний, уже лежащий в кэше ответ: кнопка нажимается, число
 * «Показано» не двигается, и это выглядит как поломка сервера.
 */
export function useNezakrytoe(filialId: number | null, pokazat?: number, filtry?: BezIshodaFiltry) {
  return useQuery<NezakrytoeScreen>({
    // Фильтры входят в ключ кэша — по тому же доводу, что у «Без исхода».
    queryKey: ['zapis', 'nezakrytoe', filialId, pokazat ?? null, filtry?.staff_id ?? null, filtry?.ot ?? null, filtry?.do ?? null],
    queryFn: () => fetchNezakrytoe(filialId, pokazat, filtry),
    /**
     * ⛔⛔ ПРЕЖНЯЯ ВЫДАЧА ДЕРЖИТСЯ НА ЭКРАНЕ, ПОКА ЕДЕТ НОВАЯ (`s341`, Н12).
     *
     * Правило заведено у журнала (`useDen`) по жалобе владельца 09.09.2026
     * «плашки куда-то пропадают» — и НЕ доехало до соседних экранов раздела.
     * Цена: фильтры и даты входят в ключ кэша, а новый ключ у `useQuery` —
     * это `isLoading`, а не `isFetching`. Страница по `isLoading` подменяет
     * ВЕСЬ экран заглушкой загрузки, панель фильтров разбирается вместе с ним
     * и приезжает обратно ЗАКРЫТОЙ.
     *
     * Так и читается находка Н12 («при попытке выбрать дату „с" — сразу
     * открывает этот день»): владелец выбирал «С даты», панель схлопывалась,
     * на экране оставалась узкая выдача — и второе поле, «По дату», выбрать
     * было уже нечем.
     */
    placeholderData: (prev) => prev,
  });
}

export function useBezIshoda(filialId: number | null, pokazat?: number, filtry?: BezIshodaFiltry) {
  return useQuery<BezIshodaScreen>({
    // ⛔ ФИЛЬТРЫ ВХОДЯТ В КЛЮЧ КЭША. Оставь их снаружи — и смена мастера
    // показала бы прежнюю выдачу из кэша, а счёт «Показано X из Y» под ней
    // считался бы по новой выборке.
    queryKey: [
      'zapis', 'bez-ishoda', filialId, pokazat ?? null,
      filtry?.staff_id ?? null, filtry?.ot ?? null, filtry?.do ?? null, filtry?.dyra ?? null,
    ],
    queryFn: () => fetchBezIshoda(filialId, pokazat, filtry),
    /**
     * ⛔⛔ ПРЕЖНЯЯ ВЫДАЧА ДЕРЖИТСЯ НА ЭКРАНЕ, ПОКА ЕДЕТ НОВАЯ (`s341`, Н12).
     *
     * Правило заведено у журнала (`useDen`) по жалобе владельца 09.09.2026
     * «плашки куда-то пропадают» — и НЕ доехало до соседних экранов раздела.
     * Цена: фильтры и даты входят в ключ кэша, а новый ключ у `useQuery` —
     * это `isLoading`, а не `isFetching`. Страница по `isLoading` подменяет
     * ВЕСЬ экран заглушкой загрузки, панель фильтров разбирается вместе с ним
     * и приезжает обратно ЗАКРЫТОЙ.
     *
     * Так и читается находка Н12 («при попытке выбрать дату „с" — сразу
     * открывает этот день»): владелец выбирал «С даты», панель схлопывалась,
     * на экране оставалась узкая выдача — и второе поле, «По дату», выбрать
     * было уже нечем.
     */
    placeholderData: (prev) => prev,
  });
}

/**
 * ⛔ ХОД КЛАДЁТ В КЭШ ОТВЕТ СЕРВЕРА, А НЕ СВОЮ ДОГАДКУ. Машина возвращает экран
 * целиком после записи; дорисуй фронт новое состояние сам — и владелец видел бы
 * не то, что лежит в базе, а то, что фронт про неё думает.
 *
 * ⛔ Соседние экраны сбрасываются: один ход меняет и день, и незакрытое, и
 * очередь. Оставь их как есть — администратор увидел бы отменённую запись живой
 * на соседней вкладке.
 */
export function useHod(id: number) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (body: HodBody) => postHod(id, body),
    onSuccess: (screen) => {
      /*
       * ⛔ СНЯТОЕ ПРАВИЛО ПРИЁМКИ В КЭШ КАРТОЧКИ НЕ КЛАДЁТСЯ (`s404`, п.6
       * довеска 13.09). Ответ хода несёт `priyomka_snyato`, а обычный запрос
       * карточки — никогда; положи его сюда — и плашка «правило снято»
       * оживала бы при каждом открытии окна из кэша, будто ход только что
       * сделан. Экран берёт её прямо у мутации, где она и родилась.
       */
      const bezPlashki = { ...screen };
      delete bezPlashki.priyomka_snyato;
      qc.setQueryData(zapisKey(id), bezPlashki);
      void qc.invalidateQueries({ queryKey: ['zapis', 'den'] });
      void qc.invalidateQueries({ queryKey: ['zapis', 'zakrytie', id] });
      void qc.invalidateQueries({ queryKey: ['zapis', 'nezakrytoe'] });
      void qc.invalidateQueries({ queryKey: ['zapis', 'ozhidanie'] });
      void qc.invalidateQueries({ queryKey: ['zapis', 'bez-ishoda'] });
    },
  });
}

/**
 * Правка закреплённого поля — заметки записи (V2.11, решение владельца 09.09).
 *
 * ⛔ ОТДЕЛЬНАЯ МУТАЦИЯ, А НЕ `useHod`. Заметка состояния не меняет: гасить из-за
 * неё кэш «Дня», «Ожидания» и «Незакрытого» значило бы перезапрашивать четыре
 * экрана из-за исправленной опечатки. Обновляется ровно карточка — и «День»,
 * потому что примечание стоит на плитке.
 */
export function usePravka(id: number) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (note: string) => postPravka(id, note),
    onSuccess: (screen) => {
      qc.setQueryData(zapisKey(id), screen);
      void qc.invalidateQueries({ queryKey: ['zapis', 'den'] });
    },
  });
}

/**
 * ПРАВКИ КАРТОЧКИ, КОТОРЫЕ НЕ ЯВЛЯЮТСЯ ХОДАМИ (`s311`, опись §11.5).
 *
 * ⛔ ОДИН УСПЕХ НА ВСЕ ПРАВКИ. Каждая из них возвращает ЦЕЛЫЙ экран записи —
 * значит и класть в кэш его надо одинаково, и журнал дня сбрасывать одинаково.
 * Разложи `onSuccess` по четырём хукам порознь — и пятый, добавленный завтра,
 * тихо забудет сбросить журнал: карточка покажет новый состав, а плитка дня —
 * старый, и владелец поверит тому, что открыл вторым.
 */
function usePravkaEkrana<T>(id: number, hod: (telo: T) => Promise<ZapisScreen>) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: hod,
    onSuccess: (screen: ZapisScreen) => {
      qc.setQueryData(zapisKey(id), screen);
      void qc.invalidateQueries({ queryKey: ['zapis', 'den'] });
    },
  });
}

/**
 * ⛔ `s1027` G-05: ПОМЕТКИ КАРТОЧКИ — ПОДСТАТУС, «ПРИНЯТА БЕЗ БРОНИ», ПОЖЕЛАНИЕ К
 * МАСТЕРУ. Они уходили голым запросом без `catch`: отказ сервера терялся, кнопки
 * не ждали ответа, и второй клик летел поверх первого. Теперь это мутации:
 * ответ (целый экран записи) ложится в карточку, соседние экраны записи
 * перечитываются — как и раньше, — а отказ и «ждём» видит страница.
 */
function usePometkaZapisi<T>(hod: (telo: { id: number } & T) => Promise<ZapisScreen>) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: hod,
    onSuccess: (screen: ZapisScreen, telo) => {
      qc.setQueryData(zapisKey(telo.id), screen);
      void qc.invalidateQueries({
        queryKey: ['zapis'],
        predicate: (q) => !(q.queryKey[1] === 'karta' && q.queryKey[2] === telo.id),
      });
    },
  });
}

/** Ступень подтверждения рукой — из карточки и с плитки журнала (`s324`). */
export function usePodstatus() {
  return usePometkaZapisi(({ id, kod }: { id: number; kod: string | null }) => postPodstatus(id, kod));
}

/** «Принята без брони» рукой (`s329`, Д13). */
export function usePrinyataBezBroni() {
  return usePometkaZapisi(({ id, stalo }: { id: number; stalo: boolean }) => postPrinyataBezBroni(id, stalo));
}

/** Пожелание к мастеру (`s329`, Д6). */
export function usePozhelanieMastera() {
  return usePometkaZapisi(({ id, kod }: { id: number; kod: 'specific' | 'any' }) => postPozhelanieMastera(id, kod));
}

/** Правка времени записи — отдельно от «Перенести» (V2.09). */
export function usePravkaVremeni(id: number) {
  return usePravkaEkrana(
    id,
    (telo: { start: string; kto?: 'client' | 'salon'; reason_id?: number; note?: string }) =>
      postPravkaVremeni(id, telo.start, telo.kto, telo.reason_id, telo.note),
  );
}

/** `s407`, находка 8: перенять состав прежней системы в свой. */
export function usePerenyatSostav(id: number) {
  return usePravkaEkrana(id, () => postSostavPerenyat(id));
}

/** «В кресле была» — ввод фактического гостя (V2.39). */
export function usePravkaGostya(id: number) {
  return usePravkaEkrana(id, (customerId: number | null) => postPravkaGostya(id, customerId));
}

/**
 * `s401`: «на что записана» + окончание по норме одной дверью.
 *
 * ⛔ Два действия одним ходом — так и задумано: выбрать процедуру и поставить
 * окончание по её норме это ОДИН поступок администратора, и след у него обязан
 * быть один. Разведи на два хода — «История изменений» покажет две правки там,
 * где человек сделал одну.
 */
export function usePostavitProceduru(id: number) {
  return usePravkaEkrana(id, (telo: { procedure_id: number; postavit_okonchanie: boolean }) =>
    postProceduru(id, telo.procedure_id, telo.postavit_okonchanie));
}

/** Живой состав: добавление позиции (О-19). */
export function useSostavDobavit(id: number) {
  return usePravkaEkrana(id, (telo: SostavDobavka) => postSostav(id, telo));
}

/**
 * Живой состав: правка строки (`s404` Д-34; `s320` Д22/Д57/Д58).
 * Количество и скидка — рублями или процентом; ею же меняется процедура строки,
 * и она же теперь правит ТОВАРНУЮ строку, а не только услугу.
 */
export function useSostavPravit(id: number) {
  return usePravkaEkrana(id, ({ stroka, ...telo }: { stroka: number } & SostavPravka) =>
    patchSostav(id, stroka, telo),
  );
}

/**
 * Удалить запись (`s320`, Д13). После удаления карточки больше нет — экран
 * уходит в журнал того же дня, а не перерисовывает удалённое.
 */
export function useUdalitZapis(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prichina?: string) => deleteZapis(id, prichina),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['zapis'] });
    },
  });
}

/** Живой состав: снятие позиции. */
export function useSostavSnyat(id: number) {
  // `s320` Д56: причина у снятия позиции необязательна — на открытой записи
  // деньги ещё не посчитаны, а у закрытой состав и так не правится.
  return usePravkaEkrana(id, ({ stroka, rod, prichina }: { stroka: number; rod: SostavRod; prichina?: string }) =>
    deleteSostav(id, stroka, rod, prichina),
  );
}

/**
 * ПЕРЕНОС И ДЛИТЕЛЬНОСТЬ ЖЕСТОМ ПО ЖУРНАЛУ (`s311`, владелец 09.09.2026).
 *
 * ⛔ ЖУРНАЛ ОБНОВЛЯЕТСЯ ВЕСЬ, А НЕ ОДНА ПЛИТКА. Перенос двигает не только саму
 * запись: у соседей меняются дорожки при пересечении, у дня — счёт по
 * состояниям. Обнови точечно — и вторая запись того же часа осталась бы стоять
 * поверх первой.
 */
export function usePerenosPlitki() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, telo }: { id: number; telo: PerenosTelo }) =>
      telo.v_ozhidanie === true
        ? postHod(id, { transition: 'park', initiated_by: telo.initiated_by })
        : postHod(id, {
          // С полки запись СТАВЯТ на время (`place`), с сетки — ПЕРЕНОСЯТ (`move`).
          transition: telo.s_polki === true ? 'place' : 'move',
          slot_start: telo.slot_start,
          slot_end: telo.slot_end,
          staff_id: telo.staff_id ?? undefined,
          /* `s322` Д54: причина приезжает из окна, которое открылось ПОСЛЕ
             отпускания плитки, — а не отказом до жеста. */
          reason_id: telo.reason_id,
          note: telo.note !== undefined && telo.note !== '' ? telo.note : undefined,
          /* ⛔ КТО ПОПРОСИЛ — ответ человека в окне переноса (владелец,
             19.09.2026). Не назвали — довод не шлётся, и сервер не угадывает. */
          initiated_by: telo.initiated_by,
        }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['zapis'] });
    },
  });
}

/** Новая длительность визита — правкой времени, а не переносом (V2.09). */
export function useDlitelnostPlitki() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, konec }: { id: number; konec: string }) => postPravkaKonca(id, konec),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['zapis'] });
    },
  });
}

/**
 * Предзапись как действие из карточки (О-23).
 *
 * ⛔ ОБЩИЙ ХЕЛПЕР ЗДЕСЬ НЕ ГОДИТСЯ, И ЭТО ЕДИНСТВЕННОЕ ИСКЛЮЧЕНИЕ (`s316`, R07).
 * Четыре его собрата возвращают экран ТОЙ ЖЕ записи — предзапись возвращает
 * экран НОВОЙ. `usePravkaEkrana` кладёт ответ под ключ исходной, и получалось
 * ровно то, что нашёл аудит: в кэше записи 101 лежит содержимое записи 202,
 * страница уходит на адрес 202, а её собственный кэш пуст. Пока экран 202
 * грузится с нуля, карточка 101 показывает чужой визит — с чужой клиенткой,
 * чужим составом и живыми кнопками поверх него.
 *
 * ⛔ ИСХОДНАЯ ЗАПИСЬ НЕ ПРОСТО ОСТАЁТСЯ — ОНА СБРАСЫВАЕТСЯ. Предзапись меняет и
 * её саму (в карточке появляется связь со следующим визитом); оставь её кэш как
 * был — и связь не показалась бы до перезагрузки страницы.
 */
/**
 * Н-06 / Н-28 — две двери карточки (`s317`).
 *
 * ⛔ ОБЕ КЛАДУТ ОТВЕТ В КЭШ КАРТОЧКИ, КАК ХОДЫ. Сервер возвращает экран целиком;
 * не положи его — и карточка показывала бы прежние пожелания до перезагрузки,
 * то есть человек не увидел бы, что его решение доехало.
 *
 * ⛔ ЖУРНАЛ ДНЯ И ЛИСТ ОЖИДАНИЯ ОБНОВЛЯЮТСЯ ТОЖЕ. «Хочу раньше» меняет ВЫБОРКУ
 * листа ожидания (`state = waiting` ∪ `wants_earlier`), отсрочка — счёт «без
 * брони» в шапке дня. Промолчи о них — и два экрана разошлись бы с третьим.
 */
export function useHochuRanshe(id: number) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (telo: HochuRansheTelo) => postHochuRanshe(id, telo),
    onSuccess: (screen: ZapisScreen) => {
      qc.setQueryData(zapisKey(screen.booking.id), screen);
      void qc.invalidateQueries({ queryKey: ['zapis', 'den'] });
      void qc.invalidateQueries({ queryKey: ['zapis', 'ozhidanie'] });
    },
  });
}

export function useOtsrochkaBroni(id: number) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (telo: OtsrochkaTelo) => postOtsrochkaBroni(id, telo),
    onSuccess: (screen: ZapisScreen) => {
      qc.setQueryData(zapisKey(screen.booking.id), screen);
      void qc.invalidateQueries({ queryKey: ['zapis', 'den'] });
    },
  });
}

export function usePredzapis(id: number) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (telo: PredzapisTelo) => postPredzapis(id, telo),
    onSuccess: (screen: ZapisScreen) => {
      qc.setQueryData(zapisKey(screen.booking.id), screen);
      void qc.invalidateQueries({ queryKey: zapisKey(id) });
      void qc.invalidateQueries({ queryKey: ['zapis', 'den'] });
    },
  });
}

/**
 * Ход над ЛЮБОЙ записью — вход разбора пачкой Э-З8.
 *
 * ⛔ ЭТО ТОТ ЖЕ ЕДИНСТВЕННЫЙ ВХОД `/zapis/:id/hod`, что у карточки, только
 * вызванный по очереди для каждой выбранной записи. Массового API нет и не
 * заводится: у каждой записи свой след R1 и своя проверка машины (`notes.md`
 * Э-З8). Отказ по одной записи не отменяет успех соседней — итог собирается
 * поштучно.
 *
 * ⛔ СПИСОК ПЕРЕЧИТЫВАЕТСЯ ПОСЛЕ ПАЧКИ, А НЕ ПРАВИТСЯ НА МЕСТЕ. После
 * подтверждённых ходов следующие необработанные записи должны попасть в первые
 * сто; дорисуй фронт новое состояние сам — и он показал бы своё представление о
 * базе вместо базы.
 */
export function useHodLyuboy() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: HodBody }) => postHod(id, body),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['zapis', 'bez-ishoda'] });
      void qc.invalidateQueries({ queryKey: ['zapis', 'den'] });
      void qc.invalidateQueries({ queryKey: ['zapis', 'nezakrytoe'] });
      void qc.invalidateQueries({ queryKey: ['zapis', 'ozhidanie'] });
    },
  });
}

/**
 * РЕЖИМ ПРИЁМКИ (`s307`, §3.1 штаба).
 *
 * ⛔ ПЕРЕКЛЮЧЕНИЕ СБРАСЫВАЕТ ВСЕ ЭКРАНЫ ЗАПИСИ. Режим меняет и баннер, и
 * доступность ходов на карточке: оставь соседние ответы в кэше — и владелец,
 * включив режим, продолжил бы упираться в «переход недопустим» на уже открытой
 * карточке.
 */
export const priyomkaKey = ['zapis', 'priyomka'] as const;

export function usePriyomka() {
  return useQuery<PriyomkaScreen>({ queryKey: priyomkaKey, queryFn: fetchPriyomka });
}

export function usePriyomkaSwitch() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (enabled: boolean) => postPriyomka(enabled),
    onSuccess: (screen) => {
      qc.setQueryData(priyomkaKey, screen);
      void qc.invalidateQueries({ queryKey: ['zapis'] });
    },
  });
}

/**
 * ЖИВЫЕ МЕТКИ МЕНЮ «ЗАПИСЬ» (`s329`, Д28).
 *
 * ⛔⛔ «СЧЁТ ЖИВОЙ, НЕ ПО ПЕРЕЗАГРУЗКЕ» — прямое требование владельца. Метка,
 * которая обновляется только при перезагрузке страницы, отвечает на вопрос
 * «что было, когда я открыл вкладку», а не «что есть сейчас»; администратор
 * держит журнал открытым весь день.
 *
 * ⛔ ПОЛМИНУТЫ — НЕ «ПОЧАЩЕ», А ТАКТ СИСТЕМЫ: с тем же шагом идёт
 * синхронизация записи с вендором (`opsmind:zapis-sync`, каждые 30 минут — но
 * живые ходы приходят сразу), и чаще спрашивать нечего: между ответами
 * ничего не меняется, а запросов стало бы в разы больше.
 */
export function useMetkiZapisi(mozhno: boolean) {
  return useQuery({
    queryKey: ['zapis', 'metki'],
    queryFn: fetchMetkiZapisi,
    enabled: mozhno,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}


/**
 * ПЕРЕПИСКА С КЛИЕНТКОЙ В КАРТОЧКЕ ЗАПИСИ (`s341`, ПР-47).
 *
 * ⛔ СВОИМ ЗАПРОСОМ, А НЕ ДОБОРОМ К КАРТОЧКЕ — по тому же правилу, что у
 * историй визитов и изменений (решение владельца 09.09): вложи переписку в
 * ответ карточки, и карточка тяжелела бы от того, чего никто не открыл.
 *
 * ⛔ ОТВЕТ ЛЁГКИЙ: номер обращения и список каналов. Саму ленту тянет
 * `useRazbor` из обращений — тот же хук, что у экрана обращений.
 */
export function usePerepiskaZapisi(bookingId: number | null) {
  return useQuery<PerepiskaZapisi>({
    queryKey: ['zapis', 'perepiska', bookingId],
    queryFn: () => fetchPerepiskaZapisi(bookingId as number),
    enabled: bookingId !== null,
    placeholderData: (prev) => prev,
  });
}

/**
 * НАПИСАТЬ КЛИЕНТКЕ ПЕРВЫМ ИЗ КАРТОЧКИ (`s341`, С-01).
 *
 * ⛔ ПОСЛЕ ХОДА ПЕРЕЧИТЫВАЮТСЯ ОБА СПИСКА — и переписка записи, и очередь
 * обращений: заведённый разговор появился и там, и там. Оставь второе — и бар
 * в шапке не узнал бы о собственном нажатии дежурного.
 */
export function useNachatPerepisku(bookingId: number) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (telo: { kind: string; channel_id?: number | null }) =>
      nachatPerepisku(bookingId, telo.kind, telo.channel_id ?? null),
    onSuccess: (otvet) => {
      qc.setQueryData(['zapis', 'perepiska', bookingId], otvet);
      void qc.invalidateQueries({ queryKey: ['obrashcheniya'] });
    },
  });
}
